import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    type OttopilotAdditionalButtonsConfig,
    ottopilotAdditionalButtonsConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import {
    type OttopilotEventArchiveConfig,
    ottopilotEventArchiveConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventArchiveConfig";
import {
    type OttopilotEventTableViewColumn,
    OttopilotEventViewPanelSectionKind,
    ottopilotEventTableViewColumnValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTableViewColumn";
import {
    type OttopilotEventContainerConstraint,
    ottopilotEventContainerConstraintValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerConstraint";
import {
    type KanbanBoardColumnConfig,
    type KanbanCardSystemFieldId,
    kanbanBoardColumnConfigValidator,
    kanbanCardSystemFieldIdValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/KanbanBoardColumnConfig";
import {
    type OttopilotDataViewerConfig,
    ottopilotDataViewerConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDataViewerConfig";
import {
    type SharedCalendarViewConfig,
    sharedCalendarViewConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/SharedCalendarViewConfig";
import {
    type OttopilotDispositionColumnConfig,
    ottopilotDispositionColumnConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDispositionColumnConfig";
import {z as zod} from "zod";

export type OttopilotEventTemplateWriteEventPageConfig = {
    formId: Uuid;
    heading: string;
    formType: "Create" | "Update";
    url?: string;
    fields: Array<
        Array<{
            id: Uuid;
            columnId: Uuid;
            label: string;
            defaultValue?: string | Uuid | Iso8601DateTime | number | boolean;
            isVisible?: boolean;
            isRequired?: boolean;
            placeHolder?: string;
            columnType: "OttopilotEvent" | "OttopilotEventContainer";
        }>
    >;
};

export type OttopilotEventTemplateEventPageConfig = {
    /**
     * Calendar configuration for this event template page.
     * When absent, calendar view is not rendered for this event template.
     */
    calendarConfig?: SharedCalendarViewConfig & {
        /**
         * Which value should be used as calendar event type (for legend and color mapping).
         */
        eventTypeSource: {
            columnType: "OttopilotEvent" | "OttopilotEventContainer";
            columnId: Uuid | "status";
        };
        /** Event or Event Container additional column holding an ISO 8601 datetime for calendar placement. */
        calendarStartDateColumnId?: Uuid;
        /** Interpolate card title from merged journey + event `additionalColumnValues`. */
        calendarTitleOttopilotStringTemplate?: string;
    };
    ottopilotDataViewerConfig?: OttopilotDataViewerConfig;
    url?: string;
    tableViewColumns?: Array<OttopilotEventTableViewColumn>;
    buttons: Array<Uuid>;
    additionalButtons?: Array<OttopilotAdditionalButtonsConfig>;
    kanbanBoardCardView: {
        heading: Array<Uuid | KanbanCardSystemFieldId>;
        details: Array<
            Array<
                {
                    iconId: Uuid;
                    label: string;
                } & (
                    | {
                          type: "SingleColumn";
                          dataSource: Uuid | KanbanCardSystemFieldId;
                      }
                    | {
                          type: "MultipleColumn";
                          seperator: string;
                          dataSource: Array<Uuid | KanbanCardSystemFieldId>;
                      }
                )
            >
        >;
    };
    kanbanBoardPageView: {
        filterFieldId: Uuid | string;
        columnConfig: Array<KanbanBoardColumnConfig>;
    };
    /**
     * ID of the additionalColumn that should be treated at the primary phone number to display the call
     * button. A value of null indicates a call button should not be drawn.
     */
    callButtonColumnId: Uuid | null;
    whatsAppButtonColumnId: Uuid | null;
    viewPanel?: {
        sections: Array<
            | {
                  kind?: OttopilotEventViewPanelSectionKind.columnGroup;
                  id: Uuid;
                  displayName: string;
                  iconId?: Uuid;
                  iconColor?: string;
                  iconBackgroundColor?: string;
                  initialIsOpen?: boolean;
                  className?: string;
                  columns: Array<
                      {
                          id: Uuid;
                          label: string;
                          columnId: Uuid | string;
                          isAdditionalColumn: boolean;
                      } & ({columnType: "OttopilotEvent"; eventTemplateId: Uuid} | {columnType: "OttopilotEventContainer"; eventTemplateId?: never})
                  >;
              }
            | {
                  kind: OttopilotEventViewPanelSectionKind.eventOverview;
                  id: Uuid;
                  displayName?: string;
                  iconId?: Uuid;
                  iconColor?: string;
                  iconBackgroundColor?: string;
                  initialIsOpen?: boolean;
                  className?: string;
              }
        >;
    };
    editPanel?: {
        columnGrouping: Array<{
            id: Uuid;
            displayName: string;
            iconId?: Uuid;
            iconColor?: string;
            iconBackgroundColor?: string;
            initialIsOpen?: boolean;
            className?: string;
            columns: Array<
                {
                    id: Uuid;
                    isEditable: boolean;
                    /** When true, mandatory (Emotorad M); UI may show * and validate when editable. */
                    isRequired?: boolean;
                    label: string;
                    columnId: Uuid | string;
                    isAdditionalColumn: boolean;
                    constraints?: Array<OttopilotEventContainerConstraint>;
                } & ({columnType: "OttopilotEvent"; eventTemplateId: Uuid} | {columnType: "OttopilotEventContainer"; eventTemplateId?: never})
            >;
        }>;
        globalConstraints?: Array<OttopilotEventContainerConstraint>;
    };
};

export type OttopilotEventTemplateConfiguration = {
    /** Required UDO reference column linking an Event to its Event Container object. */
    eventContainerColumnId: Uuid;
    /** JSON UDO column exposed as the domain object's optional notes field. */
    notesColumnId: Uuid;
    statusColumnId: Uuid;
    dispositionColumnConfig: OttopilotDispositionColumnConfig;
    displayNameVariants: {
        singularSentenceCase: string;
        pluralSentenceCase: string;
        singularLowerCase: string;
        pluralLowerCase: string;
    };
    iconName?: string;
    /**
     * Configuration for the additionalColumn that represents the status of the event.
     * Used for archiving events by updating this column to the archive status value.
     */
    statusColumnConfig?: {
        columnId: Uuid;
        archiveStatusValue: Uuid;
    };
    archiveConfig?: OttopilotEventArchiveConfig;
    writeEventPageConfig: Array<OttopilotEventTemplateWriteEventPageConfig>;
    dispositionComponentMap?: Record<Uuid, Uuid>;
    eventPage: OttopilotEventTemplateEventPageConfig;
};

const eventTemplateWriteEventPageDefaultValueValidator = zod.union([uuidZodValidator, iso8601DateTimeZodValidator, zod.string(), zod.number(), zod.boolean()]);

const eventTemplateWriteEventPageConfigValidator: zod.ZodType<OttopilotEventTemplateWriteEventPageConfig> = zod.strictObject({
    formId: uuidZodValidator,
    heading: zod.string(),
    formType: zod.enum(["Create", "Update"]),
    url: zod.string().optional(),
    fields: zod.array(
        zod.array(
            zod.strictObject({
                id: uuidZodValidator,
                columnId: uuidZodValidator,
                label: zod.string(),
                defaultValue: eventTemplateWriteEventPageDefaultValueValidator.optional(),
                isVisible: zod.boolean().optional(),
                isRequired: zod.boolean().optional(),
                placeHolder: zod.string().optional(),
                columnType: zod.enum(["OttopilotEvent", "OttopilotEventContainer"]),
            }),
        ),
    ),
});

const eventTemplateKanbanCardDataSourceValidator = zod.union([uuidZodValidator, kanbanCardSystemFieldIdValidator]);

const eventTemplateKanbanBoardCardViewValidator = zod.strictObject({
    heading: eventTemplateKanbanCardDataSourceValidator.array(),
    details: zod.array(
        zod.array(
            zod.discriminatedUnion("type", [
                zod.strictObject({
                    iconId: uuidZodValidator,
                    label: zod.string(),
                    type: zod.literal("SingleColumn"),
                    dataSource: eventTemplateKanbanCardDataSourceValidator,
                }),
                zod.strictObject({
                    iconId: uuidZodValidator,
                    label: zod.string(),
                    type: zod.literal("MultipleColumn"),
                    seperator: zod.string(),
                    dataSource: eventTemplateKanbanCardDataSourceValidator.array(),
                }),
            ]),
        ),
    ),
});

const eventTemplateCalendarConfigValidator = sharedCalendarViewConfigValidator.extend({
    eventTypeSource: zod.strictObject({
        columnType: zod.enum(["OttopilotEvent", "OttopilotEventContainer"]),
        columnId: zod.union([uuidZodValidator, zod.literal("status")]),
    }),
    calendarStartDateColumnId: uuidZodValidator.optional(),
    calendarTitleOttopilotStringTemplate: zod.string().optional(),
});

const eventTemplatePanelColumnValidator = zod.union([
    zod.strictObject({
        id: uuidZodValidator,
        label: zod.string(),
        columnId: zod.string(),
        isAdditionalColumn: zod.boolean(),
        columnType: zod.literal("OttopilotEvent"),
        eventTemplateId: uuidZodValidator,
    }),
    zod.strictObject({
        id: uuidZodValidator,
        label: zod.string(),
        columnId: zod.string(),
        isAdditionalColumn: zod.boolean(),
        columnType: zod.literal("OttopilotEventContainer"),
    }),
]);

const eventTemplateEditablePanelColumnValidator = zod.union([
    zod.strictObject({
        id: uuidZodValidator,
        isEditable: zod.boolean(),
        isRequired: zod.boolean().optional(),
        label: zod.string(),
        columnId: zod.string(),
        isAdditionalColumn: zod.boolean(),
        constraints: ottopilotEventContainerConstraintValidator.array().optional(),
        columnType: zod.literal("OttopilotEvent"),
        eventTemplateId: uuidZodValidator,
    }),
    zod.strictObject({
        id: uuidZodValidator,
        isEditable: zod.boolean(),
        isRequired: zod.boolean().optional(),
        label: zod.string(),
        columnId: zod.string(),
        isAdditionalColumn: zod.boolean(),
        constraints: ottopilotEventContainerConstraintValidator.array().optional(),
        columnType: zod.literal("OttopilotEventContainer"),
    }),
]);

const eventTemplateEventPageConfigValidator: zod.ZodType<OttopilotEventTemplateEventPageConfig> = zod.strictObject({
    calendarConfig: eventTemplateCalendarConfigValidator.optional(),
    ottopilotDataViewerConfig: ottopilotDataViewerConfigValidator.optional(),
    url: zod.string().optional(),
    tableViewColumns: ottopilotEventTableViewColumnValidator.array().optional(),
    buttons: uuidZodValidator.array(),
    additionalButtons: ottopilotAdditionalButtonsConfigValidator.array().optional(),
    kanbanBoardCardView: eventTemplateKanbanBoardCardViewValidator,
    kanbanBoardPageView: zod.strictObject({
        filterFieldId: zod.string(),
        columnConfig: kanbanBoardColumnConfigValidator.array(),
    }),
    callButtonColumnId: uuidZodValidator.nullable(),
    whatsAppButtonColumnId: uuidZodValidator.nullable(),
    viewPanel: zod
        .strictObject({
            sections: zod
                .union([
                    zod.strictObject({
                        kind: zod.literal(OttopilotEventViewPanelSectionKind.columnGroup).optional(),
                        id: uuidZodValidator,
                        displayName: zod.string(),
                        iconId: uuidZodValidator.optional(),
                        iconColor: zod.string().optional(),
                        iconBackgroundColor: zod.string().optional(),
                        initialIsOpen: zod.boolean().optional(),
                        className: zod.string().optional(),
                        columns: eventTemplatePanelColumnValidator.array(),
                    }),
                    zod.strictObject({
                        kind: zod.literal(OttopilotEventViewPanelSectionKind.eventOverview),
                        id: uuidZodValidator,
                        displayName: zod.string().optional(),
                        iconId: uuidZodValidator.optional(),
                        iconColor: zod.string().optional(),
                        iconBackgroundColor: zod.string().optional(),
                        initialIsOpen: zod.boolean().optional(),
                        className: zod.string().optional(),
                    }),
                ])
                .array(),
        })
        .optional(),
    editPanel: zod
        .strictObject({
            columnGrouping: zod
                .strictObject({
                    id: uuidZodValidator,
                    displayName: zod.string(),
                    iconId: uuidZodValidator.optional(),
                    iconColor: zod.string().optional(),
                    iconBackgroundColor: zod.string().optional(),
                    initialIsOpen: zod.boolean().optional(),
                    className: zod.string().optional(),
                    columns: eventTemplateEditablePanelColumnValidator.array(),
                })
                .array(),
            globalConstraints: ottopilotEventContainerConstraintValidator.array().optional(),
        })
        .optional(),
});

export const ottopilotEventTemplateConfigurationValidator: zod.ZodType<OttopilotEventTemplateConfiguration> = zod.strictObject({
    eventContainerColumnId: uuidZodValidator,
    notesColumnId: uuidZodValidator,
    statusColumnId: uuidZodValidator,
    dispositionColumnConfig: ottopilotDispositionColumnConfigValidator,
    displayNameVariants: zod.strictObject({
        singularSentenceCase: zod.string(),
        pluralSentenceCase: zod.string(),
        singularLowerCase: zod.string(),
        pluralLowerCase: zod.string(),
    }),
    iconName: zod.string().optional(),
    statusColumnConfig: zod
        .strictObject({
            columnId: uuidZodValidator,
            archiveStatusValue: uuidZodValidator,
        })
        .optional(),
    archiveConfig: ottopilotEventArchiveConfigValidator.optional(),
    writeEventPageConfig: eventTemplateWriteEventPageConfigValidator.array(),
    dispositionComponentMap: zod.record(uuidZodValidator, uuidZodValidator).optional(),
    eventPage: eventTemplateEventPageConfigValidator,
});
